import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { gsap } from 'gsap';
import lottie from 'lottie-web';
import fireworksData from '../../assets/lottie/fireworks.json';
import { useMobileContext } from '../../contexts/MobileContext';
import RollingNumber from '../Loaders/RollingNumber';

/** ---------------------------
 * Helpers (card shadow only)
 * --------------------------- */

/** Build a shadow filter string based on a tilt angle (deg). */
function shadowForAngle(angleDeg) {
  const t = Math.min(1, Math.max(0, Math.abs(angleDeg) / 10));
  const x1 = -4 - 10 * t;
  const y1 = 6 + 4 * t;
  const blur1 = 16 + 8 * t;
  const a1 = 0.14 + 0.08 * t;

  const x2 = 4 - 2 * t;
  const y2 = 8 - 2 * t;
  const blur2 = 14 - 2 * t;
  const a2 = 0.1 - 0.02 * t;

  return `
    drop-shadow(${x1}px ${y1}px ${blur1}px rgba(22,47,59,${a1}))
    drop-shadow(${x2}px ${y2}px ${blur2}px rgba(162,172,177,${a2}))
  `.trim();
}

/** ---------------------------
 * Main Component
 * --------------------------- */
const ProviderTileComponentSummary = forwardRef(({ data }, ref) => {
  const {
    FriendlyName,
    LogoURL,
    logoUrl, // legacy
    Excess,
    TotalPremium,
    CoverValue,
    personalLoanAcceptOffer,
    isFibreOffer,
    Amount,
    monthlyPrice,
  } = data;
  const { isMobile } = useMobileContext();

  const logoToUse = LogoURL || logoUrl || '';
  let valueToUse = '';
  if (personalLoanAcceptOffer) {
    valueToUse = Amount;
  } else if (isFibreOffer) {
    valueToUse = monthlyPrice;
  } else {
    valueToUse = TotalPremium;
  }

  // Measure width of premium row so we can align Excess / Cover
  const premiumRowRef = useRef(null);
  const [premiumWidth, setPremiumWidth] = useState(null);

  useLayoutEffect(() => {
    if (!premiumRowRef.current) return;
    const el = premiumRowRef.current;
    const measure = () => setPremiumWidth(el.getBoundingClientRect().width);
    measure();

    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, []);

  const premiumFontSize = isMobile ? 'clamp(2.25rem, 7.5vw, 3.5rem)' : '3.5rem';
  const premiumColor = '#1E354E';
  const hasValue =
    valueToUse !== null && valueToUse !== undefined && valueToUse !== '';

  // Controls the RollingNumber animation
  const [playSignal, setPlaySignal] = useState(0);

  // Layers
  const cardRef = useRef(null);
  const leftFWRef = useRef(null);
  const rightFWRef = useRef(null);
  const leftLottieRef = useRef(null);
  const rightLottieRef = useRef(null);

  const setRefs = (node) => {
    cardRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  // LOTTIE: load both sides (idle)
  useLayoutEffect(() => {
    if (leftFWRef.current) {
      leftLottieRef.current = lottie.loadAnimation({
        container: leftFWRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    if (rightFWRef.current) {
      rightLottieRef.current = lottie.loadAnimation({
        container: rightFWRef.current,
        animationData: fireworksData,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    }
    return () => {
      leftLottieRef.current?.destroy();
      rightLottieRef.current?.destroy();
      leftLottieRef.current = null;
      rightLottieRef.current = null;
    };
  }, []);

  // GSAP timeline: tilt + shadow; fireworks at 1.6s
  useLayoutEffect(() => {
    if (!cardRef.current) return;

    const playFireworks2s = (instance) => {
      if (!instance) return;
      const native = instance.getDuration?.(false);
      const speed = native && native > 0 ? native / 4 : 1; // ~3s total
      instance.setSpeed(speed);
      instance.goToAndPlay(0);
    };

    const el = cardRef.current;

    gsap.set(el, {
      transformOrigin: '50% 50%',
      rotateZ: 0,
      scale: 1,
      filter: shadowForAngle(0),
    });

    const tl = gsap.timeline({
      defaults: { ease: 'power2.inOut' },
      onStart: () => setPlaySignal((s) => s + 1),
    });

    tl.to(el, {
      duration: 1,
      rotateZ: -4,
      scale: isMobile ? 1.15 : 1.15,
      delay: 0.8,
      ease: 'power2.out',
      onUpdate: () => {
        const angle = gsap.getProperty(el, 'rotateZ');
        el.style.filter = shadowForAngle(angle);
      },
    });

    tl.to(el, {
      duration: 0.4,
      rotateZ: 0,
      scale: 1.0,
      ease: 'elastic.out(1, 0.5)',
      onUpdate: () => {
        const angle = gsap.getProperty(el, 'rotateZ');
        el.style.filter = shadowForAngle(angle);
      },
      onComplete: () => {
        el.style.filter = shadowForAngle(0);
      },
    });

    tl.add(() => {
      playFireworks2s(leftLottieRef.current);
      playFireworks2s(rightLottieRef.current);
    }, 1.6);

    return () => tl.kill();
  }, [isMobile]);

  /** Fireworks sizing/placement */
  function getDeviceScreenSize() {
    if (typeof window === 'undefined') return { width: 0, height: 0 };
    return { width: window.innerWidth, height: window.innerHeight };
  }

  const { width } = getDeviceScreenSize();
  const FW_SIZE = isMobile ? width * 0.7 : 320;
  const HALF = FW_SIZE / 2;
  const MOBILE_OFFSET = Math.round(FW_SIZE * 0.28);
  const BURST_ANCHOR_Y_DESKTOP = 0.78;
  const TOP_OFFSET_DESKTOP = Math.round(FW_SIZE * BURST_ANCHOR_Y_DESKTOP);

  return (
    <Box
      sx={{
        position: 'relative',
        width: isMobile ? '100%' : 'fit-content',
        maxWidth: '100%',
        display: 'flex',
        justifyContent: 'center',
        overflow: 'visible',
      }}
    >
      {/* LEFT fireworks (behind) */}
      <Box
        ref={leftFWRef}
        sx={{
          position: 'absolute',
          top: isMobile ? `-${HALF}px` : `-${TOP_OFFSET_DESKTOP}px`,
          left: isMobile
            ? `calc(50% - ${MOBILE_OFFSET + HALF}px)`
            : `-${HALF}px`,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'rotate(-45deg)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* RIGHT fireworks (behind) */}
      <Box
        ref={rightFWRef}
        sx={{
          position: 'absolute',
          top: isMobile ? `-${HALF}px` : `-${TOP_OFFSET_DESKTOP}px`,
          left: isMobile ? `calc(50% + ${MOBILE_OFFSET - HALF}px)` : `auto`,
          right: isMobile ? `auto` : `-${HALF}px`,
          width: `${FW_SIZE}px`,
          height: `${FW_SIZE}px`,
          transform: 'rotate(45deg)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Card (shadow wrapper - must NOT have overflow:hidden or the
          drop-shadow filter gets clipped, which is most visible on mobile) */}
      <Box
        ref={setRefs}
        sx={{
          width: isMobile ? 'min(85vw, 560px)' : '560px',
          filter: shadowForAngle(0),
          willChange: 'transform, filter',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Clip wrapper - owns overflow:hidden/borderRadius so the logo and
            content stay clipped without cutting off the shadow above */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: hasValue
              ? 'minmax(0, 0.45fr) minmax(0, 0.55fr)'
              : '1fr',
            rowGap: hasValue ? (isMobile ? '12px' : 0) : 0,
            overflow: 'hidden',
            alignItems: 'stretch',
            borderRadius: '24px',
            padding: '16px',
            backgroundColor: '#FFFFFF',
            boxSizing: 'border-box',
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              minHeight: isMobile ? '82px' : '86px',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              px: '8px',
              boxSizing: 'border-box',
              gridColumn: hasValue ? 'auto' : '1 / -1',
            }}
          >
            <img
              src={logoToUse}
              alt={FriendlyName}
              style={{
                maxWidth: '80%',
                maxHeight: !hasValue ? '60px' : '90px',
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Premium + Excess */}
          {hasValue && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                px: '12px',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              {/* Premium row (RollingNumber) */}
              <Box
                ref={premiumRowRef}
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  width: 'fit-content',
                  mx: 'auto',
                }}
              >
                <RollingNumber
                  value={valueToUse}
                  duration={2.4}
                  extraLoops={3}
                  playSignal={playSignal}
                  animate={true}
                  prefix={{ text: 'R', sx: {} }}
                  suffix={
                    personalLoanAcceptOffer
                      ? { text: '', sx: {} }
                      : {
                          text: 'pm',
                          sx: {
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            ml: '4px',
                            color: premiumColor,
                            lineHeight: 1,
                          },
                        }
                  }
                  digitSx={{
                    fontSize: premiumFontSize,
                    color: premiumColor,
                    fontWeight: 700,
                    lineHeight: 1,
                    letterSpacing: '-0.02em',
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'right',
                    mb: '4px',
                  }}
                />
              </Box>

              {/* Excess + Cover lines aligned to premium width */}
              <Box
                sx={{
                  alignSelf: 'center',
                  width: premiumWidth ? `${premiumWidth}px` : 'auto',
                }}
              >
                {Excess && (
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      fontSize: '1rem',
                      lineHeight: '120%',
                      color: '#A4ACB0',
                      textAlign: 'left',
                    }}
                  >
                    Excess R{parseInt(Excess, 10).toLocaleString('en-ZA')}
                  </Typography>
                )}
                {CoverValue && (
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      fontSize: '1rem',
                      lineHeight: '120%',
                      color: '#A4ACB0',
                      textAlign: 'left',
                      mt: Excess ? '2px' : 0,
                    }}
                  >
                    R{parseInt(CoverValue, 10).toLocaleString('en-ZA')} Cover
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
});

ProviderTileComponentSummary.displayName = 'ProviderTileComponentSummary';
export default ProviderTileComponentSummary;
